/**
 * LogoReveal — cinematic brand intro for the homepage (Sprint 19).
 *
 * A lightweight, CSS-driven 6.5s overlay: charcoal intro with gold particles,
 * iridescent hummingbird flying in on a curved path from the left, landing on
 * the metallic A, then the ASIA SOURCING wordmark reveals left-to-right with a
 * metallic shine sweep, followed by the tagline and trust line.
 *
 * Performance: transform/opacity keyframes only (GPU-accelerated), three small
 * PNG assets composited with mix-blend-mode: screen. pointer-events: none —
 * the site is usable immediately; the overlay unmounts after the sequence.
 * Accessibility: honours prefers-reduced-motion (no overlay is rendered) and
 * the sequence is aria-hidden decorative. "ASIA SOURCING" is real HTML text.
 */
import { useEffect, useState } from "react";

const LETTERS = "ASIA SOURCING".split("");

export const LogoReveal = () => {
  const [done, setDone] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setDone(true); return undefined; }
    const t = setTimeout(() => setDone(true), 7300);
    return () => clearTimeout(t);
  }, []);

  if (done) return null;

  return (
    <div className="logo-reveal" aria-hidden="true" role="presentation" data-testid="logo-reveal">
      {/* Scene 1 · atmospheric gold particles */}
      <span className="lr-particle lr-p1" />
      <span className="lr-particle lr-p2" />
      <span className="lr-particle lr-p3" />
      <span className="lr-particle lr-p4" />
      <span className="lr-particle lr-p5" />
      <span className="lr-particle lr-p6" />

      <div className="lr-lockup">
        {/* Scenes 2-4 · hummingbird flies in, approaches, lands on the A */}
        <div className="lr-mark-wrap">
          <img src="/brand/anim-a-mark.png" alt="" className="lr-mark" />
          <div className="lr-mark-glow" />
          <div className="lr-bird-flight">
            <span className="lr-trail lr-t1" />
            <span className="lr-trail lr-t2" />
            <span className="lr-trail lr-t3" />
            <img src="/brand/anim-bird.png" alt="" className="lr-bird" />
          </div>
        </div>

        {/* Scenes 5-6 · wordmark + taglines reveal (real HTML text) */}
        <div className="lr-text">
          <div className="lr-word" aria-label="Asia Sourcing">
            {LETTERS.map((ch, i) =>
              ch === " " ? (
                <span key={i} className="lr-space" />
              ) : (
                <span key={i} className="lr-letter" style={{ "--i": i }}>{ch}</span>
              )
            )}
            <span className="lr-shine" />
          </div>
          <p className="lr-tagline">Sourcing a brighter tomorrow</p>
          <p className="lr-trust">
            <span>Verified sources</span><i>|</i><span>Quality assured</span><i>|</i><span>Global reach</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LogoReveal;
